import { cn } from "@/lib/utils/cn";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";

const Root = ({ ...props }: React.ComponentProps<typeof BaseDialog.Root>) => (
  <BaseDialog.Root data-slot="sheet" {...props} />
);

const Trigger = ({ ...props }: React.ComponentProps<typeof BaseDialog.Trigger>) => (
  <BaseDialog.Trigger data-slot="sheet-trigger" {...props} />
);

const Close = ({ ...props }: React.ComponentProps<typeof BaseDialog.Close>) => (
  <BaseDialog.Close data-slot="sheet-close" {...props} />
);

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseDialog.Backdrop>) => (
  <BaseDialog.Backdrop
    data-slot="sheet-overlay"
    className={cn(
      "fixed inset-0 z-50 bg-black/30 transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
      className,
    )}
    {...props}
  />
);

const sheetVariants = cva(
  // No `gap`: spacing comes from the self-padded Header/Body/Footer parts so a
  // form wrapper (which collapses to a single flex child) lays out the same as
  // header/body/footer placed directly under Content.
  "fixed z-50 flex flex-col bg-background shadow-lg transition-[transform,translate] duration-300 ease-in-out",
  {
    variants: {
      side: {
        top:
          "inset-x-0 top-0 border-b data-[starting-style]:-translate-y-full data-[ending-style]:-translate-y-full",
        bottom:
          "inset-x-0 bottom-0 border-t data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full",
        left:
          "inset-y-0 left-0 h-full border-r data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full",
        right:
          "inset-y-0 right-0 h-full border-l data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full",
      },
      size: {
        default: "",
        lg: "",
        xl: "",
        "2xl": "",
        "3xl": "",
      },
    },
    compoundVariants: [
      { side: ["left", "right"], size: "default", className: "w-3/4 sm:max-w-sm" },
      { side: ["left", "right"], size: "lg", className: "w-5/6 sm:max-w-lg" },
      { side: ["left", "right"], size: "xl", className: "w-full sm:max-w-2xl" },
      { side: ["left", "right"], size: "2xl", className: "w-full sm:max-w-4xl" },
      { side: ["left", "right"], size: "3xl", className: "w-full sm:max-w-6xl" },
    ],
    defaultVariants: {
      side: "right",
      size: "default",
    },
  },
);

type SheetContentProps =
  & { hideOverlay?: boolean; }
  & React.ComponentProps<typeof BaseDialog.Popup>
  & VariantProps<typeof sheetVariants>;

const Content = (
  { children, className, side, size, hideOverlay = false, ...props }: SheetContentProps,
) => (
  <BaseDialog.Portal>
    {!hideOverlay && <SheetOverlay />}
    <BaseDialog.Popup
      data-slot="sheet-content"
      className={cn(sheetVariants({ side, size }), className)}
      {...props}
    >
      <BaseDialog.Close
        data-slot="sheet-close-icon"
        className="focus-ring absolute top-4 right-4 flex size-7 items-center justify-center rounded-lg border border-transparent text-muted-foreground transition-all hover:text-foreground disabled:pointer-events-none cursor-pointer"
      >
        <XIcon className="size-4" />
        <span className="sr-only">Close</span>
      </BaseDialog.Close>
      {children}
    </BaseDialog.Popup>
  </BaseDialog.Portal>
);

const Header = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-header"
    className={cn("flex flex-col gap-1.5 border-b px-6 pt-6 pb-4", className)}
    {...props}
  />
);
Header.displayName = "SheetHeader";

// The scrollable middle region. `min-h-0` lets it shrink inside the flex column
// so `overflow-y-auto` actually kicks in; `flex-1` makes it fill between a fixed
// Header and Footer.
const Body = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-body"
    className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)}
    {...props}
  />
);
Body.displayName = "SheetBody";

const Footer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-footer"
    className={cn(
      "mt-auto flex flex-row items-center justify-end gap-2 border-t px-6 py-4",
      className,
    )}
    {...props}
  />
);
Footer.displayName = "SheetFooter";

const Title = ({ className, ...props }: React.ComponentProps<typeof BaseDialog.Title>) => (
  <BaseDialog.Title
    data-slot="sheet-title"
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
);

const Description = (
  { className, ...props }: React.ComponentProps<typeof BaseDialog.Description>,
) => (
  <BaseDialog.Description
    data-slot="sheet-description"
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);

export const Sheet = Object.assign(Root, {
  Trigger,
  Close,
  Content,
  Header,
  Body,
  Footer,
  Title,
  Description,
});
